import axios from "axios";
import { useState, useRef, useEffect, startTransition } from "react";
import cookieStorage from "../../cookieStorage";
import {
    API_ENDPOINT,
    getAuthHeaders,
    convertIntoNumber,
    registerCartActions
} from "./shared";

export default function useCartApi() {
    const [cart, setCart] = useState({});
    const cartRef = useRef({});
    const cartMutationChain = useRef(Promise.resolve());
    const pendingCartMutations = useRef([]);
    const cartCoalesceTimer = useRef(null);
    const cartSnapshotBeforeBatch = useRef(null);
    const cartSyncInFlight = useRef(false);
    const cartNeedsResync = useRef(false);
    const lastCartSyncResponse = useRef(null);
    const extendCartTimeTimer = useRef(null);
    const cartTimeRef = useRef(0);

    const [dontShowTimeExtendAlert, setDontShowTimeExtendAlert] = useState(
        false
    );
    const [cartTime, setCartTime] = useState(0);
    const [cartProducts, setCartProducts] = useState({});
    const [productByBatch, setProductByBatch] = useState({});
    const [pricing, setPricing] = useState(null);
    const [linePricingByProductId, setLinePricingByProductId] = useState({});
    const [cartAmount, setCartAmount] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [originalPrice, setOriginalPrice] = useState(0);
    const [ordering, setOrdering] = useState(false);
    const [showGamificationPopOver, setShowGamificationPopOver] = useState(
        false
    );
    const [cartOpen, setCartOpen] = useState(false);

    useEffect(() => {
        cartRef.current = cart;
    }, [cart]);

    useEffect(() => {
        cartTimeRef.current = cartTime;
    }, [cartTime]);

    const CART_SYNC_IDLE_MS = 500;

    const getCustomerIdFallback = () =>
        cartRef.current.customerId || cookieStorage.getItem("customerId");

    const cloneCart = (cartData) =>
        cartData && Object.keys(cartData).length
            ? JSON.parse(JSON.stringify(cartData))
            : {};

    // Fallback for older API responses that don't include a pricing breakdown:
    // derive totals from cart line quantities and cached product prices.
    const computeFallbackPricing = (cartData, cartProductsData) => {
        let price = 0;
        let originalTotal = 0;
        (cartData.products || []).forEach((cartLine) => {
            price += Number(
                cartLine.quantity *
                    cartProductsData[cartLine.productId]?.DefaultPrice?.value
            );
            originalTotal += Number(
                cartLine.quantity *
                    cartProductsData[cartLine.productId]?.OriginalPrice?.value
            );
        });
        return { price, originalTotal };
    };

    const mergeCartProducts = (serverProducts, localProducts) => {
        const productMap = new Map();
        for (const product of serverProducts || []) {
            productMap.set(product.productId, { ...product });
        }
        for (const product of localProducts || []) {
            if (productMap.has(product.productId)) {
                const existing = productMap.get(product.productId);
                productMap.set(product.productId, {
                    ...existing,
                    quantity: Math.max(existing.quantity, product.quantity)
                });
            } else {
                productMap.set(product.productId, { ...product });
            }
        }
        return Array.from(productMap.values());
    };

    const applyOptimisticCart = (payload) => {
        const current = cartRef.current;
        const nextCart = {
            ...current,
            customerId: payload.data.customerId,
            sessionId: payload.data.sessionId ?? payload.data.customerId,
            products: payload.data.products
        };
        if (payload.type === "update" && payload.cartId) {
            nextCart._id = payload.cartId;
        }
        cartRef.current = nextCart;
        startTransition(() => setCart(nextCart));
    };

    const applyLightCartSync = (response, type) => {
        const serverCart =
            type === "add" ? response.data.savedCart : response.data.updatedCart;
        const mergedProducts = mergeCartProducts(
            serverCart.products,
            cartRef.current.products
        );
        const cartData = { ...serverCart, products: mergedProducts };
        cartRef.current = cartData;
        startTransition(() => setCart(cartData));
        lastCartSyncResponse.current = { response, type };
    };

    const applyDeferredCartDetails = () => {
        const last = lastCartSyncResponse.current;
        if (!last) {
            return;
        }
        const { response, type } = last;
        setCartProducts(response.data.cartProducts);
        setProductByBatch(response.data.productByBatch);
        setPricing(response.data.pricing || null);
        setLinePricingByProductId(response.data.linePricingByProductId || {});
        const syncedCart =
            type === "add" ? response.data.savedCart : response.data.updatedCart;
        if (response.data.pricing) {
            setTotalPrice(response.data.pricing.total || 0);
            setOriginalPrice(response.data.pricing.subtotal || 0);
        } else if (Object.keys(syncedCart || {}).length) {
            const { price, originalTotal } = computeFallbackPricing(
                syncedCart,
                response.data.cartProducts || {}
            );
            setTotalPrice(price);
            setOriginalPrice(originalTotal);
        }
        setCartAmount(syncedCart?.products?.length || 0);
        if (type === "update") {
            setCartTime(
                convertIntoNumber(response.data.updatedCart.SessionTime)
            );
        }
        lastCartSyncResponse.current = null;
    };

    const scheduleExtendCartTime = () => {
        clearTimeout(extendCartTimeTimer.current);
        extendCartTimeTimer.current = setTimeout(() => {
            const time = cartTimeRef.current;
            if (
                time < 20 * 60 &&
                time > 0 &&
                (cartRef.current.products || []).length > 0
            ) {
                axios
                    .put(
                        `${API_ENDPOINT}/carts/${getCustomerIdFallback()}/addTime`,
                        "",
                        { headers: getAuthHeaders() }
                    )
                    .then((response) => {
                        setCartTime(convertIntoNumber(response.data.SessionTime));
                    })
                    .catch(() => {});
            }
        }, 800);
    };

    const resolvePendingMutations = (responseData) => {
        const batch = pendingCartMutations.current.splice(0);
        batch.forEach(({ resolve }) => resolve(responseData));
        cartSnapshotBeforeBatch.current = null;
    };

    const rejectPendingMutations = (err) => {
        const batch = pendingCartMutations.current.splice(0);
        batch.forEach(({ reject }) => reject(err));
        cartSnapshotBeforeBatch.current = null;
    };

    const syncCartToServer = async () => {
        if (cartSyncInFlight.current) {
            cartNeedsResync.current = true;
            return;
        }

        cartSyncInFlight.current = true;
        const snapshot =
            cartSnapshotBeforeBatch.current || cloneCart(cartRef.current);

        try {
            let responseData;
            do {
                cartNeedsResync.current = false;
                const currentCart = cartRef.current;
                const products = [...(currentCart.products || [])];
                const sentProductsKey = JSON.stringify(products);

                if (!products.length) {
                    break;
                }

                if (currentCart._id) {
                    const response = await axios.put(
                        `${API_ENDPOINT}/carts/${currentCart._id}`,
                        {
                            sessionId: currentCart.customerId,
                            customerId: currentCart.customerId,
                            products
                        },
                        { headers: getAuthHeaders() }
                    );
                    applyLightCartSync(response, "update");
                    responseData = response.data;
                } else {
                    const response = await axios.post(
                        `${API_ENDPOINT}/carts`,
                        {
                            sessionId: currentCart.customerId || getCustomerIdFallback(),
                            customerId: currentCart.customerId || getCustomerIdFallback(),
                            products
                        },
                        { headers: getAuthHeaders() }
                    );
                    applyLightCartSync(response, "add");
                    responseData = response.data;
                }

                const hasNewChanges =
                    cartNeedsResync.current ||
                    JSON.stringify(cartRef.current.products || []) !==
                        sentProductsKey;

                if (!hasNewChanges) {
                    break;
                }
            } while (true);

            resolvePendingMutations(responseData);
            applyDeferredCartDetails();
            scheduleExtendCartTime();
        } catch (error) {
            const err = error.response?.data || error;
            const revertedCart = snapshot || {};
            cartRef.current = revertedCart;
            startTransition(() => setCart(revertedCart));
            lastCartSyncResponse.current = null;
            rejectPendingMutations(err);
        } finally {
            cartSyncInFlight.current = false;
            if (
                cartNeedsResync.current ||
                pendingCartMutations.current.length
            ) {
                cartNeedsResync.current = false;
                cartMutationChain.current = cartMutationChain.current.then(() =>
                    syncCartToServer()
                );
            }
        }
    };

    const scheduleCartSync = () => {
        if (cartSyncInFlight.current) {
            cartNeedsResync.current = true;
            return;
        }
        clearTimeout(cartCoalesceTimer.current);
        cartCoalesceTimer.current = setTimeout(() => {
            cartCoalesceTimer.current = null;
            cartMutationChain.current = cartMutationChain.current.then(() =>
                syncCartToServer()
            );
        }, CART_SYNC_IDLE_MS);
    };

    const applyCartState = (response, type) => {
        const cartData =
            type === "add" ? response.data.savedCart : response.data.updatedCart;
        cartRef.current = cartData;
        setCart(cartData);
        setCartProducts(response.data.cartProducts);
        setProductByBatch(response.data.productByBatch);
        setPricing(response.data.pricing || null);
        setLinePricingByProductId(response.data.linePricingByProductId || {});
        if (response.data.pricing) {
            setTotalPrice(response.data.pricing.total || 0);
            setOriginalPrice(response.data.pricing.subtotal || 0);
        } else if (Object.keys(cartData || {}).length) {
            const { price, originalTotal } = computeFallbackPricing(
                cartData,
                response.data.cartProducts || {}
            );
            setTotalPrice(price);
            setOriginalPrice(originalTotal);
        }
        setCartAmount(cartData?.products?.length || 0);
        if (type === "update") {
            setCartTime(
                convertIntoNumber(response.data.updatedCart.SessionTime)
            );
        }
    };

    const mutateCart = (productId, buildPayload) => {
        if (
            !cartSnapshotBeforeBatch.current &&
            !cartSyncInFlight.current
        ) {
            cartSnapshotBeforeBatch.current = cloneCart(cartRef.current);
        }

        const payload = buildPayload(cartRef.current);
        if (!payload) {
            return Promise.reject({ message: "Unable to update cart" });
        }

        applyOptimisticCart(payload);

        if (cartSyncInFlight.current) {
            cartNeedsResync.current = true;
        }

        return new Promise((resolve, reject) => {
            pendingCartMutations.current.push({
                productId,
                resolve,
                reject
            });
            scheduleCartSync();
        });
    };

    registerCartActions({ mutateCart, setCartOpen });

    return {
        cart,
        setCart,
        cartTime,
        setCartTime,
        cartProducts,
        productByBatch,
        pricing,
        linePricingByProductId,
        setLinePricingByProductId,
        cartAmount,
        setCartAmount,
        totalPrice,
        setTotalPrice,
        originalPrice,
        setOriginalPrice,
        cartOpen,
        setCartOpen,
        ordering,
        setOrdering,
        showGamificationPopOver,
        setShowGamificationPopOver,
        dontShowTimeExtendAlert,
        setDontShowTimeExtendAlert,
        mutateCart,

        getCart(id) {
            return new Promise((resolve, reject) => {
                axios
                    .get(`${API_ENDPOINT}/carts/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        setCart(response.data.cart);
                        setCartProducts(response.data.cartProducts);
                        setProductByBatch(response.data.productByBatch);
                        setPricing(response.data.pricing || null);
                        setLinePricingByProductId(response.data.linePricingByProductId || {});
                        if (response.data.pricing) {
                            setTotalPrice(response.data.pricing.total || 0);
                            setOriginalPrice(response.data.pricing.subtotal || 0);
                        } else if (Object.keys(response.data.cart || {}).length) {
                            const { price, originalTotal } = computeFallbackPricing(
                                response.data.cart,
                                response.data.cartProducts || {}
                            );
                            setTotalPrice(price);
                            setOriginalPrice(originalTotal);
                        }
                        setCartAmount(response.data.cart?.products?.length || 0);
                        setCartTime(
                            convertIntoNumber(response.data.cart.SessionTime)
                        );
                    })
                    .catch((error) => {
                        // reject(error.response.data);
                        setCart({});
                        setCartProducts({});
                        setProductByBatch({});
                        setPricing(null);
                        setLinePricingByProductId({});
                        setCartAmount(0);
                        setTotalPrice(0);
                        setOriginalPrice(0);
                    });
            });
        },
        addCart(data) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`${API_ENDPOINT}/carts`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        applyCartState(response, "add");
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        updateCart(id, data) {
            return new Promise((resolve, reject) => {
                axios
                    .put(`${API_ENDPOINT}/carts/${id}`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                        applyCartState(response, "update");
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        removeCart(id) {
            return new Promise((resolve, reject) => {
                axios
                    .delete(`${API_ENDPOINT}/carts/${id}`, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        addOrder(data) {
            return new Promise((resolve, reject) => {
                axios
                    .post(`${API_ENDPOINT}/orders`, data, {
                        headers: getAuthHeaders()
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        applyDiscount(customerId, discountCode) {
            return new Promise((resolve, reject) => {
                axios
                    .post(
                        `${API_ENDPOINT}/orders/${customerId}/applyDiscount`,
                        { discountCode },
                        { headers: getAuthHeaders() }
                    )
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        rebuildCart(id) {
            return new Promise((resolve, reject) => {
                axios
                    .post(
                        `${API_ENDPOINT}/carts/${id}/${getCustomerIdFallback()}/rebuildCart`,
                        {},
                        { headers: getAuthHeaders() }
                    )
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        reviewOrder() {
            return new Promise((resolve, reject) => {
                axios
                    .post(
                        `${API_ENDPOINT}/orders/${getCustomerIdFallback()}/reviewOrder`,
                        {},
                        { headers: getAuthHeaders() }
                    )
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        },
        extendCartTime() {
            return new Promise((resolve, reject) => {
                axios
                    .put(
                        `${API_ENDPOINT}/carts/${getCustomerIdFallback()}/addTime`,
                        "",
                        { headers: getAuthHeaders() }
                    )
                    .then((response) => {
                        resolve(response.data);
                        setCartTime(
                            convertIntoNumber(response.data.SessionTime)
                        );
                    })
                    .catch((error) => {
                        reject(error.response.data);
                    });
            });
        }
    };
}
